import { useEffect, useState } from 'react';
import { evaluateProgramme, programmeDuChapitre, simulateSemaine } from '@neopolis/engine';
import type { Content, GameState } from '@neopolis/engine';

import { T } from '../../textes-interface.js';

/** Un jour toutes les 600 ms : assez lent pour être lu, assez court pour tenir en séance. */
const DUREE_PAR_JOUR = 600;

/**
 * Étape 3 — Tester le programme sur une semaine.
 *
 * C'est ici que se joue C9 : mettre au point. L'îlot ne reçoit pas un verdict,
 * il regarde sept jours défiler et voit lui-même les journées où l'eau coule
 * pour rien — le lendemain de l'orage, ou en pleine chaleur. Ce que la
 * simulation montre, aucune phrase de correction ne le remplacerait.
 *
 * Les jours apparaissent un à un, et le bilan n'arrive qu'à la fin : on veut
 * qu'ils soient lus, pas survolés.
 */
export function EtapeSemaine({ content, partie }: { content: Content; partie: GameState }) {
  const [jourAffiche, setJourAffiche] = useState<number | null>(null);
  const semaine = simulateSemaine(partie, content);
  const evaluation = evaluateProgramme(partie, content);

  // Les unités sont déclarées avec les grandeurs, dans le contenu : les écrire
  // ici les rendrait intraduisibles et incohérentes avec l'écran précédent.
  const grandeurs = programmeDuChapitre(content, partie.chapterId)?.reglages.grandeurs ?? [];
  const uniteDe = (id: string): string => grandeurs.find((g) => g.id === id)?.unite ?? '';

  const enCours = jourAffiche !== null && jourAffiche < semaine.jours.length;

  useEffect(() => {
    if (!enCours) return;
    const minuteur = setTimeout(() => setJourAffiche((n) => (n ?? 0) + 1), DUREE_PAR_JOUR);
    return () => clearTimeout(minuteur);
  }, [enCours, jourAffiche]);

  if (partie.programme === null) {
    return <p className="font-semibold text-slate-700">{T.semaine.aucunProgramme}</p>;
  }

  const visibles = semaine.jours.slice(0, jourAffiche ?? 0);
  const termine = jourAffiche !== null && jourAffiche >= semaine.jours.length;

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-bold text-slate-900">{T.semaine.titre}</h2>
        <button
          type="button"
          disabled={enCours}
          onClick={() => setJourAffiche(0)}
          className="min-h-touch rounded-xl bg-sky-600 px-4 font-semibold text-white hover:bg-sky-700 disabled:bg-slate-300"
        >
          {enCours
            ? T.semaine.enCours
            : jourAffiche === null
              ? T.semaine.lancer
              : T.semaine.relancer}
        </button>
      </header>

      <div className="overflow-x-auto rounded-2xl border-2 border-slate-200 bg-white">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b-2 border-slate-300">
              <th className="p-3">{T.semaine.colonneJour}</th>
              <th className="p-3">{T.semaine.colonneMeteo}</th>
              <th className="p-3">{T.semaine.colonneSol}</th>
              <th className="p-3">{T.semaine.colonneArrosage}</th>
              <th className="p-3">{T.semaine.colonneSoir}</th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((jour) => (
              <tr
                key={jour.jour}
                className={`border-b border-slate-200 ${jour.plantesEnSouffrance ? 'bg-rose-50' : ''}`}
              >
                <td className="p-3 font-semibold capitalize">{jour.jour}</td>
                <td className="p-3">
                  {jour.temperature} {uniteDe('temperature')}
                  {jour.pluie_mm > 0 && (
                    <span className="text-sky-700">
                      {' '}
                      · {jour.pluie_mm} {T.semaine.pluie}
                    </span>
                  )}
                </td>
                <td className="p-3 tabular-nums">
                  {Math.round(jour.humiditeMatin)} {uniteDe('humidite_sol')}
                </td>
                <td className="p-3">
                  {jour.arrose ? (
                    <>
                      <span className="font-semibold">{T.semaine.arrose}</span>
                      <span className="text-slate-600">
                        {' '}
                        — {Math.round(jour.litres)} {T.semaine.litres}
                      </span>
                      {jour.litresPerdus > 0 && (
                        <span className="text-rose-700">
                          {' '}
                          ({Math.round(jour.litresPerdus)} {T.semaine.perdus})
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-slate-500">{T.semaine.pasArrose}</span>
                  )}
                </td>
                <td className="p-3 tabular-nums">
                  {Math.round(jour.humiditeSoir)} {uniteDe('humidite_sol')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {termine && (
        <div className="space-y-3 rounded-2xl border-2 border-sky-200 bg-sky-50 p-4">
          <dl className="grid gap-3 sm:grid-cols-3">
            <div>
              <dt className="text-sm text-slate-600">{T.semaine.bilanEau}</dt>
              <dd className="text-2xl font-bold tabular-nums text-slate-900">
                {Math.round(semaine.litresTotal)} {T.semaine.litres}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-slate-600">{T.semaine.bilanPerdue}</dt>
              <dd className="text-2xl font-bold tabular-nums text-rose-700">
                {Math.round(semaine.litresPerdusTotal)} {T.semaine.litres}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-slate-600">{T.semaine.bilanSouffrance}</dt>
              <dd className="text-2xl font-bold tabular-nums text-slate-900">
                {semaine.joursEnSouffrance}
              </dd>
            </div>
          </dl>

          {semaine.joursEnSouffrance === 0 && (
            <p className="font-semibold text-emerald-800">{T.semaine.plantesOk}</p>
          )}

          {evaluation.variante && (
            <p
              aria-label={`${T.semaine.etoiles} : ${evaluation.variante.etoiles} / 3`}
              className="text-2xl"
            >
              <span aria-hidden="true">
                {'★'.repeat(evaluation.variante.etoiles) +
                  '☆'.repeat(3 - evaluation.variante.etoiles)}
              </span>
            </p>
          )}

          <p className="text-slate-800">{evaluation.message}</p>
        </div>
      )}
    </section>
  );
}
